import React, { useEffect, useState } from "react";
import { getDatabase, onValue, ref, push, set } from "firebase/database";
import { useAccount } from "../context/AccountContext";
import { useGetCurrentUserId } from "../hooks/useGetCurrentUserId";
import { useSearchParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Download, Info, AlertTriangle, CheckCircle } from "lucide-react";
import "./UserTransportOrders.css";
import { db } from "../Firebase/firebase-config";

const UserTransportOrders = ({ inline = false }) => {
  const { currentAccount } = useAccount();
  const { userId: currentUserId } = useGetCurrentUserId();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [orders, setOrders] = useState([]);
  const [podsData, setPodsData] = useState({});
  const [invoicesData, setInvoicesData] = useState({});
  const [reportedIssues, setReportedIssues] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedDispatches, setSelectedDispatches] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [showPageInfo, setShowPageInfo] = useState(false);

  // Shortage Report State
  const [showShortageModal, setShowShortageModal] = useState(false);
  const [shortageQty, setShortageQty] = useState("");
  const [shortageUnit, setShortageUnit] = useState("bags"); // "bags" | "kg"
  const [selectedTruckForShortage, setSelectedTruckForShortage] = useState(null);
  const [submittingShortage, setSubmittingShortage] = useState(false);
  const [isViewMode, setIsViewMode] = useState(false);

  // Toaster
  const [toastMessage, setToastMessage] = useState("");

  const highlightOrderId = searchParams.get("orderId");
  const effectiveUid = currentAccount?.uid || currentUserId;
  const clientName =
    currentAccount?.company_name || currentAccount?.name || "Client";

  // ---------- Fetch Orders + Documents + Existing Issues ----------
  useEffect(() => {
    if (!effectiveUid) return;

    const dbInstance = getDatabase();

    // 1. Orders
    const ordersRef = ref(dbInstance, "transport_orders");
    const unsubOrders = onValue(ordersRef, (snapshot) => {
      const data = snapshot.val();
      if (!data) {
        setOrders([]);
        return;
      }

      const allOrders = [];
      Object.entries(data).forEach(([transporterId, transporterOrders]) => {
        Object.entries(transporterOrders || {}).forEach(([orderId, order]) => {
          const isMyOrder =
            order.clientUid === effectiveUid ||
            order.clientId === effectiveUid ||
            (order.clientName || order.companyName) ===
            currentAccount?.company_name;

          if (isMyOrder) {
            allOrders.push({ id: orderId, transporterId, ...order });
          }
        });
      });

      allOrders.sort(
        (a, b) => (b.orderCreatedAt || 0) - (a.orderCreatedAt || 0)
      );
      setOrders(allOrders);
    });

    // 2. PODs & Invoices
    const docsRef = ref(dbInstance, "transport_documents");
    const unsubDocs = onValue(
      docsRef,
      (snapshot) => {
        const podMap = {};
        const invoiceMap = {};

        if (!snapshot.exists()) {
          setPodsData({});
          setInvoicesData({});
          setLoading(false);
          return;
        }

        const allTransporters = snapshot.val();

        Object.entries(allTransporters || {}).forEach(
          ([transporterId, transporterDocs]) => {
            if (!transporterDocs || typeof transporterDocs !== "object")
              return;

            Object.entries(transporterDocs).forEach(
              ([orderId, orderLevel]) => {
                if (!orderLevel || typeof orderLevel !== "object") return;

                // POD
                if (orderLevel.POD && typeof orderLevel.POD === "object") {
                  Object.entries(orderLevel.POD).forEach(([podId, pod]) => {
                    if (!pod || typeof pod !== "object") return;

                    if (!podMap[orderId]) podMap[orderId] = [];
                    podMap[orderId].push({
                      ...pod,
                      podId,
                      orderId: pod.orderId || orderId,
                      transporterId: pod.transporterId || transporterId,
                    });
                  });
                }

                // INVOICES
                if (
                  orderLevel.invoices &&
                  typeof orderLevel.invoices === "object"
                ) {
                  Object.entries(orderLevel.invoices).forEach(
                    ([invoiceId, invoice]) => {
                      if (!invoice || typeof invoice !== "object") return;

                      if (!invoiceMap[orderId]) invoiceMap[orderId] = [];
                      invoiceMap[orderId].push({
                        ...invoice,
                        invoiceId,
                        orderId: invoice.orderId || orderId,
                        transporterId:
                          invoice.transporterId || transporterId,
                      });
                    }
                  );
                }
              }
            );
          }
        );

        setPodsData(podMap);
        setInvoicesData(invoiceMap);
        setLoading(false);
      },
      (error) => {
        console.error("Error fetching transport documents:", error);
        setPodsData({});
        setInvoicesData({});
        setLoading(false);
      }
    );

    // 3. Existing Issues
    const issuesRef = ref(dbInstance, "transport_issues");
    const unsubIssues = onValue(issuesRef, (snapshot) => {
      const data = snapshot.val();
      if (!data) {
        setReportedIssues({});
        return;
      }

      const reported = {};
      Object.values(data).forEach((transporterIssues) => {
        if (!transporterIssues) return;
        Object.values(transporterIssues).forEach((issue) => {
          if (
            issue &&
            String(issue.clientId).trim() === String(effectiveUid).trim()
          ) {
            const key = `${issue.orderId}_${issue.truckNumber || ""}`;
            if (
              !reported[key] ||
              (issue.reportedAt || 0) > (reported[key].reportedAt || 0)
            ) {
              reported[key] = issue;
            }
          }
        });
      });
      setReportedIssues(reported);
    });

    return () => {
      unsubOrders();
      unsubDocs();
      unsubIssues();
    };
  }, [effectiveUid, currentAccount?.company_name]);

  // ---------- Open Shortage Modal (new report) ----------
  const openShortageModal = (dispatch, orderContext) => {
    const key = `${orderContext.orderId}_${dispatch.truckNumber || ""}`;
    if (reportedIssues[key]) return;

    setSelectedTruckForShortage({
      ...dispatch,
      orderId: orderContext.orderId,
      transporterId: orderContext.transporterId,
      from: orderContext.from,
      to: orderContext.to,
      orderQuantity: orderContext.orderQuantity,
      totalLoad: orderContext.totalLoad,
      transporterName: orderContext.transporterName,
    });
    setShortageQty("");
    setShortageUnit("bags");
    setIsViewMode(false);
    setShowShortageModal(true);
  };

  // ---------- Open View Mode (already reported) ----------
  const openViewReportedIssue = (dispatch, orderContext) => {
    const key = `${orderContext.orderId}_${dispatch.truckNumber || ""}`;
    const existingIssue = reportedIssues[key];
    if (!existingIssue) return;

    setSelectedTruckForShortage({
      ...dispatch,
      orderId: orderContext.orderId,
      transporterId: orderContext.transporterId,
      from: orderContext.from,
      to: orderContext.to,
      orderQuantity: orderContext.orderQuantity,
      totalLoad: orderContext.totalLoad,
      transporterName: orderContext.transporterName,
      existingIssue,
    });
    setShortageQty("");
    setShortageUnit("bags");
    setIsViewMode(true);
    setShowShortageModal(true);
  };

  // ---------- Submit Shortage Report ----------
  const handleSubmitShortage = async () => {
    if (!selectedTruckForShortage) return;

    const qty = shortageQty.trim();
    if (!qty || Number(qty) <= 0) {
      setToastMessage("Please enter a valid shortage quantity.");
      setTimeout(() => setToastMessage(""), 3000);
      return;
    }

    setSubmittingShortage(true);

    try {
      const dbInstance = getDatabase();
      const now = Date.now();

      const {
        truckNumber,
        truckId,
        driverNumber,
        load,
        unit,
        createdAt,
        orderId,
        transporterId,
        from,
        to,
      } = selectedTruckForShortage;

      const pod = (podsData[orderId] || []).find(
        (p) => p.truckId === truckId || p.truckNumber === truckNumber
      );

      const unitLabel = shortageUnit === "kg" ? "kg" : "bags";
      const issueText = `Shortage of ${qty} ${unitLabel}.`;

      const issueData = {
        clientId: effectiveUid,
        clientName: clientName,
        transporterId,
        orderId,
        truckNumber: truckNumber || "-",
        truckId: truckId || null,
        driverNumber: driverNumber || "-",
        load: load || "-",
        unit: unit || "",
        dispatchDate: createdAt || null,
        // podUrl: pod?.fileUrl || null,
        route: `${from || "-"} → ${to || "-"}`,
        from: from || "-",
        to: to || "-",
        issue: issueText,
        // shortageQty: qty,
        // shortageUnit: unitLabel, 
        // shortageBags: shortageUnit === "bags" ? qty : null, 
        reportedAt: now,
        // status: "Open",
      };

      // 1. Save issue
      const issuesRef = ref(dbInstance, `transport_issues/${transporterId}`);
      const newIssueRef = push(issuesRef);
      await set(newIssueRef, {
        ...issueData,
        issueId: newIssueRef.key,
      });

      // 2. Notification for Transporter
      const transporterNotifRef = ref(
        dbInstance,
        `transport_notifications/${transporterId}`
      );
      const transporterNotifPush = push(transporterNotifRef);
      await set(transporterNotifPush, {
        category: "issue",
        createdAt: now,
        gifUrl: "/Images/New notification.gif",
        isSupplier: true,
        message: `${clientName} reported shortage of ${qty} ${unitLabel} on Order #${orderId} for Truck ${truckNumber || "-"
          }.`,
        pageTitle: "Transport Issues",
        read: false,
        timestamp: now,
        title: "Shortage Reported",
      });

      // 3. supplier_notifications
      const supplierNotifRef = ref(
        dbInstance,
        `supplier_notifications/Transport_Issue/${effectiveUid}`
      );
      const supplierNotifPush = push(supplierNotifRef);
      await set(supplierNotifPush, {
        category: "Issue",
        gifUrl: "/Images/New notification.gif",
        message: `${clientName} reported shortage of ${qty} ${unitLabel} on Order #${orderId} for Truck ${truckNumber || "-"
          }.`,
        seen: false,
        timestamp: new Date().toISOString(),
        title: "Shortage Reported",
      });

      setToastMessage("Shortage reported successfully!");
      setTimeout(() => setToastMessage(""), 3500);

      setShowShortageModal(false);
      setSelectedTruckForShortage(null);
      setShortageQty("");
      setShortageUnit("bags");
      setIsViewMode(false);
    } catch (err) {
      console.error("Error reporting shortage:", err);
      setToastMessage("Failed to report shortage. Please try again.");
      setTimeout(() => setToastMessage(""), 3500);
    } finally {
      setSubmittingShortage(false);
    }
  };

  const filteredOrders = orders.filter((order) => {
    const status = (order.status || "").toLowerCase();
    return !statusFilter || status === statusFilter.toLowerCase();
  });

  const statusOptions = [
    ...new Set(orders.map((o) => o.status).filter(Boolean)),
  ];

  if (loading) {
    return <div className="tps-loading">Loading your transport orders...</div>;
  }

  return (
    <div className="tp-placeholder-content">
      <div className="tps-container">
        {/* Toast */}
        {toastMessage && (
          <div className="tps-toast">
            <CheckCircle size={18} />
            <span>{toastMessage}</span>
          </div>
        )}

        {/* ========== HEADER ========== */}
        <div className="tps-header">
          <div className="ccr-topbar mb-1">
            <button
              className="trans-back-button"
              onClick={() => navigate("/dashboard/transport")}
            >
              <ArrowLeft size={20} />
              Back to  Dashboard
            </button>
            {!inline && (
              <button
                onClick={() => navigate("/dashboard/order-status")}
                className="tps-for-orders-link"
              >
                View FOR Orders
              </button>
            )}
          </div>


          <div className="tps-header-top">
            <h1>EX Orders</h1>
            <button
              onClick={() => setShowPageInfo(!showPageInfo)}
              className="tps-info-btn"
              title="Page information"
            >
              <Info size={20} />
            </button>
          </div>

          {showPageInfo && (
            <div className="tps-page-info-modal">
              <div className="tps-page-info-content">
                <button
                  onClick={() => setShowPageInfo(false)}
                  className="tps-info-close"
                >
                  ✕
                </button>
                <h3>Transport Orders Overview</h3>
                <ul>
                  <li>View all transport orders placed with transporters</li>
                  <li>Track order status (In-Process, Completed, etc.)</li>
                  <li>
                    View dispatch history and Proof of Delivery and Invoices
                  </li>
                  <li>Filter orders by client or status</li>
                  <li>
                    Orders automatically highlight when viewed from quotes page
                  </li>
                  <li>Report shortage against any truck dispatch</li>
                </ul>
              </div>
            </div>
          )}

          <div className="tps-filters">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="tps-select"
            >
              <option value="">All Status</option>
              {statusOptions.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* ========== TABLE VIEW ========== */}
        <div className="tps-table-wrapper">
          <table className="tps-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Order ID</th>
                <th>Quantity</th>
                <th>Route</th>
                <th>Status</th>
                <th>Transporter</th>
                <th>Phone</th>
                <th>Dispatches</th>
                <th>Created</th>
                <th>Updated</th>
              </tr>
            </thead>
            <tbody>
              {filteredOrders.length === 0 ? (
                <tr>
                  <td colSpan="10" className="tps-no-data">
                    No transport orders found
                  </td>
                </tr>
              ) : (
                filteredOrders.map((order, index) => {
                  const isHighlighted =
                    (highlightOrderId &&
                      (order.orderId === highlightOrderId ||
                        order.id === highlightOrderId)) ||
                    order.status?.toLowerCase() === "in-process";

                  return (
                    <tr
                      key={order.id}
                      className={
                        highlightOrderId &&
                          (order.orderId === highlightOrderId ||
                            order.id === highlightOrderId)
                          ? "tps-row-highlight"
                          : order.status?.toLowerCase() === "in-process"
                            ? "tps-row-inprocess"
                            : ""
                      }
                    >
                      <td>{index + 1}</td>
                      <td
                        className={isHighlighted ? "tps-cell-highlight" : ""}
                      >
                        {order.orderId || order.id || "-"}
                      </td>
                      <td>
                        {order.quantity} {order.quantityUnit}
                      </td>
                      <td>
                        {order.from || "-"} → {order.to || "-"}
                      </td>
                      <td>
                        <span className="tps-status">
                          {order.status || "Pending"}
                        </span>
                      </td>
                      <td>{order.transporterName || "-"}</td>
                      <td>{order.transporterPhone || "-"}</td>
                      <td>
                        <button
                          className="tps-btn-view"
                          onClick={() =>
                            setSelectedDispatches({
                              ...(order.dispatch || {}),
                              orderId: order.orderId || order.id,
                              transporterId: order.transporterId,
                              from: order.from,
                              to: order.to,
                              orderQuantity: order.quantity,
                              totalLoad: order.dispatch?.totalLoad,
                              transporterName: order.transporterName,
                            })
                          }
                        >
                          View
                        </button>
                      </td>
                      <td>
                        {new Date(
                          order.orderCreatedAt ||
                          order.createdAt ||
                          Date.now()
                        ).toLocaleDateString()}
                      </td>
                      <td>
                        {new Date(
                          order.lastUpdated ||
                          order.orderCreatedAt ||
                          Date.now()
                        ).toLocaleDateString()}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* ========== CARD VIEW ========== */}
        <div className="tps-cards-wrapper">
          {filteredOrders.length === 0 ? (
            <div className="tps-no-data-cards">No transport orders found</div>
          ) : (
            filteredOrders.map((order, index) => (
              <div
                key={order.id}
                className={`tps-card ${highlightOrderId &&
                    (order.orderId === highlightOrderId ||
                      order.id === highlightOrderId)
                    ? "tps-card-highlight"
                    : order.status?.toLowerCase() === "in-process"
                      ? "tps-card-inprocess"
                      : ""
                  }`}
              >
                <div className="tps-card-header">
                  <div className="tps-card-title">Order #{index + 1}</div>
                  <span className="tps-status">
                    {order.status || "Pending"}
                  </span>
                </div>
                <div className="tps-card-body">
                  <div className="tps-card-row">
                    <span className="tps-card-label">Order ID:</span>
                    <span className="tps-card-value">
                      {order.orderId || order.id || "-"}
                    </span>
                  </div>
                  <div className="tps-card-row">
                    <span className="tps-card-label">Quantity:</span>
                    <span className="tps-card-value">
                      {order.quantity} {order.quantityUnit}
                    </span>
                  </div>
                  <div className="tps-card-row">
                    <span className="tps-card-label">Route:</span>
                    <span className="tps-card-value">
                      {order.from || "-"} → {order.to || "-"}
                    </span>
                  </div>
                  <div className="tps-card-row">
                    <span className="tps-card-label">Transporter:</span>
                    <span className="tps-card-value">
                      {order.transporterName || "-"}
                    </span>
                  </div>
                  <div className="tps-card-row">
                    <span className="tps-card-label">Phone:</span>
                    <span className="tps-card-value">
                      {order.transporterPhone || "-"}
                    </span>
                  </div>
                  <div className="tps-card-row">
                    <span className="tps-card-label">Created:</span>
                    <span className="tps-card-value">
                      {new Date(
                        order.orderCreatedAt ||
                        order.createdAt ||
                        Date.now()
                      ).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="tps-card-row">
                    <span className="tps-card-label">Updated:</span>
                    <span className="tps-card-value">
                      {new Date(
                        order.lastUpdated ||
                        order.orderCreatedAt ||
                        Date.now()
                      ).toLocaleDateString()}
                    </span>
                  </div>
                </div>
                <div className="tps-card-footer">
                  <button
                    className="tps-btn-view tps-btn-full"
                    onClick={() =>
                      setSelectedDispatches({
                        ...(order.dispatch || {}),
                        orderId: order.orderId || order.id,
                        transporterId: order.transporterId,
                        from: order.from,
                        to: order.to,
                        orderQuantity: order.quantity,
                        totalLoad: order.dispatch?.totalLoad,
                        transporterName: order.transporterName,
                      })
                    }
                  >
                    View Dispatches
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* ========== DISPATCH POPUP ========== */}
        {selectedDispatches && (
          <div
            className="tps-modal-overlay"
            onClick={() => setSelectedDispatches(null)}
          >
            <div className="tps-modal" onClick={(e) => e.stopPropagation()}>
              <div className="tps-modal-header">
                <h3>
                  Dispatch History - Order #{selectedDispatches.orderId}
                </h3>
                <button
                  className="tps-btn-close"
                  onClick={() => setSelectedDispatches(null)}
                >
                  ×
                </button>
              </div>

              {!selectedDispatches?.items ? (
                <p className="tps-no-dispatch">No dispatches assigned yet.</p>
              ) : (
                <>
                  <div className="tps-dispatch-summary">
                    <div className="tps-summary-item">
                      <div className="tps-summary-label">From</div>
                      <div className="tps-summary-value">
                        {selectedDispatches.from || "-"}
                      </div>
                    </div>
                    <div className="tps-summary-item">
                      <div className="tps-summary-label">To</div>
                      <div className="tps-summary-value">
                        {selectedDispatches.to || "-"}
                      </div>
                    </div>
                    <div className="tps-summary-item">
                      <div className="tps-summary-label">Order Qty</div>
                      <div className="tps-summary-value">
                        {selectedDispatches.orderQuantity || "-"}
                      </div>
                    </div>
                    <div className="tps-summary-item">
                      <div className="tps-summary-label">Total Load</div>
                      <div className="tps-summary-value">
                        {selectedDispatches.totalLoad || "-"}
                      </div>
                    </div>
                    <div className="tps-summary-item">
                      <div className="tps-summary-label">Order ID</div>
                      <div className="tps-summary-value">
                        {selectedDispatches.orderId}
                      </div>
                    </div>
                  </div>

                  <div className="tps-dispatch-table-wrapper">
                    <table className="tps-dispatch-table">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Truck No.</th>
                          <th>Driver No.</th>
                          <th>Load</th>
                          <th>Date & Time</th>
                          <th>Proof of Delivery</th>
                          <th>Report Shortage</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.values(selectedDispatches.items || {}).map(
                          (dispatch, index) => {
                            const pod = (
                              podsData[selectedDispatches.orderId] || []
                            ).find(
                              (p) =>
                                p.truckId === dispatch.truckId ||
                                p.truckNumber === dispatch.truckNumber
                            );

                            const reportKey = `${selectedDispatches.orderId}_${dispatch.truckNumber || ""
                              }`;
                            const alreadyReported = !!reportedIssues[reportKey];

                            return (
                              <tr key={index}>
                                <td>{index + 1}</td>
                                <td>{dispatch.truckNumber || "-"}</td>
                                <td>{dispatch.driverNumber || "-"}</td>
                                <td>
                                  {dispatch.load || "-"} {dispatch.unit || ""}
                                </td>
                                <td>
                                  {dispatch.createdAt
                                    ? new Date(
                                      dispatch.createdAt
                                    ).toLocaleString()
                                    : "-"}
                                </td>
                                <td>
                                  {pod ? (
                                    <a
                                      href={pod.fileUrl}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="tps-proof-link"
                                    >
                                      <Download size={16} /> View POD
                                    </a>
                                  ) : (
                                    <span className="tps-no-proof">
                                      No POD yet
                                    </span>
                                  )}
                                </td>
                                <td>
                                  {alreadyReported ? (
                                    <button
                                      className="tps-reported-badge tps-reported-clickable"
                                      onClick={() =>
                                        openViewReportedIssue(dispatch, {
                                          orderId: selectedDispatches.orderId,
                                          transporterId:
                                            selectedDispatches.transporterId,
                                          from: selectedDispatches.from,
                                          to: selectedDispatches.to,
                                          orderQuantity:
                                            selectedDispatches.orderQuantity,
                                          totalLoad:
                                            selectedDispatches.totalLoad,
                                          transporterName:
                                            selectedDispatches.transporterName,
                                        })
                                      }
                                    >
                                      <CheckCircle size={14} /> Reported
                                    </button>
                                  ) : (
                                    <button
                                      className="tps-btn-shortage"
                                      onClick={() =>
                                        openShortageModal(dispatch, {
                                          orderId: selectedDispatches.orderId,
                                          transporterId:
                                            selectedDispatches.transporterId,
                                          from: selectedDispatches.from,
                                          to: selectedDispatches.to,
                                          orderQuantity:
                                            selectedDispatches.orderQuantity,
                                          totalLoad:
                                            selectedDispatches.totalLoad,
                                          transporterName:
                                            selectedDispatches.transporterName,
                                        })
                                      }
                                    >
                                      <AlertTriangle size={14} /> Report
                                    </button>
                                  )}
                                </td>
                              </tr>
                            );
                          }
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Invoices Section */}
                  {invoicesData[selectedDispatches.orderId]?.length > 0 && (
                    <div className="tps-documents-section">
                      <div className="tps-pod-box">
                        <div className="tps-section-header">
                          <h4>Invoices</h4>
                          <span className="invoice-count">
                            {
                              invoicesData[selectedDispatches.orderId]
                                .length
                            }
                          </span>
                        </div>
                        <div className="invoice-list">
                          {invoicesData[selectedDispatches.orderId].map(
                            (inv, i) => (
                              <a
                                key={i}
                                href={inv.fileUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="invoice-card"
                              >
                                <div className="invoice-left">
                                  <div className="invoice-icon">
                                    <Download size={18} />
                                  </div>
                                  <div className="invoice-details">
                                    <span className="invoice-name">
                                      {inv.fileName ||
                                        inv.billNumber ||
                                        `Invoice ${i + 1}`}
                                    </span>
                                    <span className="invoice-subtitle">
                                      Click to download
                                    </span>
                                  </div>
                                </div>
                                <Download
                                  size={18}
                                  className="invoice-download-icon"
                                />
                              </a>
                            )
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {/* ========== SHORTAGE REPORT / VIEW MODAL ========== */}
        {showShortageModal && selectedTruckForShortage && (
          <div
            className="tps-modal-overlay"
            onClick={() =>
              !submittingShortage && setShowShortageModal(false)
            }
          >
            <div
              className="tps-modal tps-shortage-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="tps-modal-header">
                <h3>
                  {isViewMode
                    ? "Reported Shortage – "
                    : "Report Shortage – "}
                  Truck {selectedTruckForShortage.truckNumber}
                </h3>
                <button
                  className="tps-btn-close"
                  disabled={submittingShortage}
                  onClick={() => {
                    setShowShortageModal(false);
                    setIsViewMode(false);
                  }}
                >
                  ×
                </button>
              </div>

              <div className="tps-shortage-body">
                {/* Order / Truck Info */}
                <div className="tps-shortage-info">
                  <p>
                    <strong>Order:</strong> #
                    {selectedTruckForShortage.orderId}
                  </p>
                  <p>
                    <strong>Driver:</strong>{" "}
                    {selectedTruckForShortage.driverNumber || "-"}
                  </p>
                  <p>
                    <strong>Load:</strong>{" "}
                    {selectedTruckForShortage.load || "-"}{" "}
                    {selectedTruckForShortage.unit || ""}
                  </p>
                  <p>
                    <strong>Route:</strong>{" "}
                    {selectedTruckForShortage.from} →{" "}
                    {selectedTruckForShortage.to}
                  </p>
                </div>

                {/* ===== Previous Report ===== */}
                {isViewMode && selectedTruckForShortage.existingIssue && (
                  <div className="tps-previous-report">
                    <div className="tps-previous-title">
                      <CheckCircle size={16} /> Previous Report
                    </div>
                    <div className="tps-previous-content">
                      <p>
                        <strong>Shortage:</strong>{" "}
                        {selectedTruckForShortage.existingIssue
                          .shortageQty ||
                          selectedTruckForShortage.existingIssue
                            .shortageBags ||
                          "-"}{" "}
                        {selectedTruckForShortage.existingIssue
                          .shortageUnit || "bags"}
                      </p>
                      <p>
                        <strong>Issue:</strong>{" "}
                        {selectedTruckForShortage.existingIssue.issue ||
                          "-"}
                      </p>
                      <p>
                        <strong>Reported At:</strong>{" "}
                        {new Date(
                          selectedTruckForShortage.existingIssue
                            .reportedAt
                        ).toLocaleString()}
                      </p>
                    </div>
                  </div>
                )}

                {/* ===== Unit Selection ===== */}
                <div className="tps-form-group">
                  <label>Unit *</label>

                  <div
                    className={`tps-unit-toggle ${shortageUnit === "kg" ? "kg-active" : "bags-active"
                      } ${submittingShortage ? "disabled" : ""}`}
                    onClick={() => {
                      if (!submittingShortage) {
                        setShortageUnit(
                          shortageUnit === "bags" ? "kg" : "bags"
                        );
                      }
                    }}
                  >
                    <div className="tps-unit-toggle-slider"></div>

                    <span className="tps-unit-label bags-label">
                      Bags
                    </span>

                    <span className="tps-unit-label kg-label">
                      Kg
                    </span>
                  </div>
                </div>

                {/* ===== Quantity Input ===== */}
                <div className="tps-form-group">
                  <label>
                    {isViewMode
                      ? `Report Another Shortage (${shortageUnit === "kg" ? "Kg" : "Bags"
                      }) *`
                      : `Shortage Quantity (${shortageUnit === "kg" ? "Kg" : "Bags"
                      }) *`}
                  </label>
                  <input
                    type="number"
                    min="1"
                    step="any"
                    placeholder={
                      shortageUnit === "kg" ? "e.g. 250" : "e.g. 12"
                    }
                    value={shortageQty}
                    onChange={(e) => setShortageQty(e.target.value)}
                    disabled={submittingShortage}
                    autoFocus
                    onWheel={(e) => e.target.blur()}
                  />
                </div>

                <div className="tps-shortage-actions">
                  <button
                    className="tps-btn-cancel"
                    onClick={() => {
                      setShowShortageModal(false);
                      setIsViewMode(false);
                    }}
                    disabled={submittingShortage}
                  >
                    Cancel
                  </button>
                  <button
                    className="tps-btn-submit-shortage"
                    onClick={handleSubmitShortage}
                    disabled={submittingShortage}
                  >
                    {submittingShortage
                      ? "Submitting..."
                      : isViewMode
                        ? "Submit New"
                        : "Submit"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserTransportOrders;